import { useEffect, useMemo } from 'react';

import { getMsUntilNextSecond, seed } from '#shared';

import type { NewCase } from '#entities/types';

import { usePollingQuery, useAggregateRefetch } from '#shared/api';
import type { PollingQueryResult } from '#shared/api';

import { useGetNewCasesQuery } from '../newCases.api';

const TARGET_SECOND = 5;

const getNewCasesDelay = () => getMsUntilNextSecond(TARGET_SECOND);

// DEFAULT ON; REACT_APP_EVENT_DRIVEN=false reverts to legacy timer polling. When
// on, WS entity frames keep data live so polling is neutralized via `skip` (rules-of-hooks forbids a conditional hook call).
const DEFAULT_EVENT_DRIVEN = import.meta.env.REACT_APP_EVENT_DRIVEN !== 'false';

export const useQueryNewCases = (
  eventDriven: boolean = DEFAULT_EVENT_DRIVEN,
): PollingQueryResult<NewCase[]> => {
  const queryResult = useGetNewCasesQuery(undefined, {
    pollingInterval: 0,
  });

  const { getDelay, refetchSignal } = useAggregateRefetch(
    'newCases',
    getNewCasesDelay,
  );
  const pollingResult = usePollingQuery(queryResult, {
    getDelay,
    refetchSignal,
    skip: eventDriven,
  });

  useEffect(() => {
    if (!eventDriven) {
      return;
    }
    const cursor = pollingResult.data?.cursor;
    if (cursor) {
      seed('case', [cursor]);
    }
  }, [eventDriven, pollingResult.data]);

  return useMemo(
    () => ({
      data: pollingResult.data?.data ?? [],
      generatedAt: pollingResult.data?.generatedAt ?? null,
      validation: pollingResult.data?.validation ?? null,
      isLoading: pollingResult.isLoading,
      isFetching: pollingResult.isFetching,
      isError: pollingResult.isError,
      error: pollingResult.error,
      lastSentTime: pollingResult.lastSentTime,
      lastReceiveTime: pollingResult.lastReceiveTime,
    }),
    [pollingResult],
  );
};
